import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey, phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  InventoryVendorCreateSchema,
  InventoryVendorQueryAllSchema,
  InventoryVendorQuerySchema,
  InventoryVendorUpdateSchema,
} from './schemas';

export const useGetInventoryVendorsMutation: UseControlledMutation<InventoryVendorQuerySchema> = () =>
  useMutation({
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORY_VENDORS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryVendorQueryAllSchema>({
            endpoint: '/api/v1/vendors',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetInventoryVendors(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_VENDORS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<InventoryVendorQueryAllSchema>({
        endpoint: '/api/v1/vendors?include=vendor_w9_file',
        params: `${apiParamStr}`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INVENTORY_VENDORS'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, InventoryVendorCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/vendors/',
        payload,
      }),
  });

export function useGetInventoryVendor(id: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_VENDOR', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InventoryVendorQuerySchema }>({
        endpoint: `/api/v1/vendors/${id}`,
        params: { include: `${phoneNumberIncludeKey()},vendor_w9_file,${addressesIncludeKey()}` },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InventoryVendorQuerySchema }, ApiError, Partial<InventoryVendorUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/vendors/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVENTORY_VENDOR_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/vendors/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
